export type AccountType = 'BANK_ACCOUNT' | 'CASH_MANAGEMENT';
export type Insurance = 'FDIC' | 'NCUA' | 'SIPC' | 'NONE';

export interface SavingsAccount {
  id: string;
  institution: string;
  product: string;
  type: AccountType;
  insurance: Insurance;
  insuranceNote?: string;
  apyBps: number; // BASE annual percentage yield (standing rate, no promo), basis points
  bonusBps?: number; // promotional bonus on top of the base rate, basis points
  minToEarn: number; // minimum balance to earn stated APY, dollars
  monthlyFee: number; // dollars
  bonusText?: string; // conditions for the bonus or top rate
  url: string;
  rateSourceUrl: string; // page the monthly audit screenshots
  asOf: string; // ISO date the rate was last verified
  featured: boolean; // appears in the comparison table
  isReserve?: boolean;
  region?: string; // 2-letter state code for local/regional institutions (picker grouping)
}

export const RATES_AS_OF = '2026-08-23';

// Sources for the featured set: NerdWallet "Best High-Yield Savings Accounts"
// and CNBC Select "Best High-Yield Savings Accounts" roundups.
// Rates are refreshed by the monthly audit (scripts/rate-audit): see asOf.
export const ACCOUNTS: SavingsAccount[] = [
  {
    id: 'futuremoney-reserve',
    institution: 'FutureMoney',
    product: 'Reserve',
    type: 'CASH_MANAGEMENT',
    insurance: 'SIPC',
    insuranceNote: 'Securities in your account are protected by SIPC up to $500,000. Not a bank account; not FDIC insured.',
    apyBps: 450, // 3.60 base + 0.25 IRA match + 0.65 welcome boost (display only; real quote is computed)
    minToEarn: 0,
    monthlyFee: 0,
    bonusText: '+0.65% welcome boost & +0.25% IRA match',
    url: 'https://futuremoney.co/reserve',
    rateSourceUrl: 'https://futuremoney.co/reserve',
    asOf: RATES_AS_OF,
    featured: true,
    isReserve: true,
  },
  {
    id: 'openbank',
    institution: 'Openbank by Santander',
    product: 'High Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 380,
    minToEarn: 500,
    monthlyFee: 0,
    url: 'https://www.openbank.us',
    rateSourceUrl: 'https://www.openbank.us/savings',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'bask',
    institution: 'Bask Bank',
    product: 'Interest Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 375,
    bonusBps: 35,
    bonusText: 'Boosts require $2,500+/mo qualifying automated deposits',
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.baskbank.com',
    rateSourceUrl: 'https://www.baskbank.com/interest-savings',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'everbank',
    institution: 'EverBank',
    product: 'Performance Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 390,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.everbank.com',
    rateSourceUrl: 'https://www.everbank.com/banking/savings',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'ufb',
    institution: 'UFB Direct',
    product: 'Portfolio Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 326,
    bonusBps: 20,
    bonusText: 'Boost requires linked UFB Freedom Checking; $10/mo fee waived at $5,000+',
    minToEarn: 0,
    monthlyFee: 10,
    url: 'https://www.ufbdirect.com',
    rateSourceUrl: 'https://www.ufbdirect.com/savings-account',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'barclays',
    institution: 'Barclays',
    product: 'Tiered Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 350,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.banking.barclaysus.com',
    rateSourceUrl: 'https://www.banking.barclaysus.com/tiered-savings.html',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'cit',
    institution: 'CIT Bank',
    product: 'Platinum Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 375,
    bonusBps: 35,
    bonusText: 'Promo code CITBOOST, 6 months, $5,000+ balance',
    minToEarn: 5000,
    monthlyFee: 0,
    url: 'https://www.cit.com',
    rateSourceUrl: 'https://www.cit.com/cit-bank/bank/savings/platinum-savings',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'wealthfront',
    institution: 'Wealthfront',
    product: 'Cash Account',
    type: 'CASH_MANAGEMENT',
    insurance: 'FDIC',
    insuranceNote: 'FDIC insurance passed through partner banks (up to $8M).',
    apyBps: 330,
    bonusBps: 65,
    bonusText: 'New-customer boost, 3 months, on up to $150,000',
    minToEarn: 1,
    monthlyFee: 0,
    url: 'https://www.wealthfront.com/cash',
    rateSourceUrl: 'https://www.wealthfront.com/cash',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'sofi',
    institution: 'SoFi',
    product: 'Checking & Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 310,
    bonusBps: 70,
    bonusText: 'New-member boost, up to 6 months; requires direct deposit or $5,000+/mo deposits',
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.sofi.com/banking',
    rateSourceUrl: 'https://www.sofi.com/banking/savings-account',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'marcus',
    institution: 'Marcus by Goldman Sachs',
    product: 'Online Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 340,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.marcus.com',
    rateSourceUrl: 'https://www.marcus.com/us/en/savings/high-yield-savings',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'betterment',
    institution: 'Betterment',
    product: 'Cash Reserve',
    type: 'CASH_MANAGEMENT',
    insurance: 'FDIC',
    insuranceNote: 'FDIC insurance passed through program banks (up to $4M).',
    apyBps: 325,
    bonusBps: 75,
    bonusText: 'New-customer boost with qualifying deposit, balances up to $1M',
    minToEarn: 10,
    monthlyFee: 0,
    url: 'https://www.betterment.com/cash-reserve',
    rateSourceUrl: 'https://www.betterment.com/cash-reserve',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'synchrony',
    institution: 'Synchrony Bank',
    product: 'High Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 330,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.synchronybank.com',
    rateSourceUrl: 'https://www.synchronybank.com/banking/high-yield-savings',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'amex',
    institution: 'American Express',
    product: 'High Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 300,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.americanexpress.com/en-us/banking/online-savings',
    rateSourceUrl: 'https://www.americanexpress.com/en-us/banking/online-savings/account-home',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'discover',
    institution: 'Discover',
    product: 'Online Savings (now Capital One 360)',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 300,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.discover.com/online-banking/savings-account',
    rateSourceUrl: 'https://www.discover.com/online-banking/savings-account',
    asOf: RATES_AS_OF,
    featured: false,
  },
  {
    id: 'capitalone',
    institution: 'Capital One',
    product: '360 Performance Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 300,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.capitalone.com/bank/savings-accounts/online-performance-savings-account',
    rateSourceUrl: 'https://www.capitalone.com/bank/savings-accounts/online-performance-savings-account',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'ally',
    institution: 'Ally Bank',
    product: 'Online Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 300,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.ally.com/bank/online-savings-account',
    rateSourceUrl: 'https://www.ally.com/bank/online-savings-account',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'newtek',
    institution: 'Newtek Bank',
    product: 'Personal High Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 420,
    bonusText: 'New accounts currently wait-listed',
    minToEarn: 100,
    monthlyFee: 0,
    url: 'https://www.newtekbank.com/personal/high-yield-savings/',
    rateSourceUrl: 'https://www.newtekbank.com/personal/high-yield-savings/',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'lendingclub',
    institution: 'LendingClub Bank',
    product: 'LevelUp Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 300,
    bonusBps: 100,
    bonusText: 'LevelUp rate requires $250+/mo in deposits',
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.happen.com/personal-savings/founder-savings',
    rateSourceUrl: 'https://www.happen.com/personal-savings/founder-savings',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'bread',
    institution: 'Bread Savings',
    product: 'High-Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 395,
    minToEarn: 100,
    monthlyFee: 0,
    url: 'https://www.breadfinancial.com/en/bread-savings.html',
    rateSourceUrl: 'https://www.breadfinancial.com/en/bread-savings.html',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'populardirect',
    institution: 'Popular Direct',
    product: 'Exclusive Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 390,
    minToEarn: 100,
    monthlyFee: 0,
    url: 'https://www.populardirect.com/products/savings/',
    rateSourceUrl: 'https://www.populardirect.com/products/savings/',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'briodirect',
    institution: 'BrioDirect',
    product: 'High-Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 375,
    minToEarn: 5000,
    monthlyFee: 0,
    url: 'https://www.briodirectbanking.com/',
    rateSourceUrl: 'https://www.briodirectbanking.com/',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'bmoalto',
    institution: 'BMO Alto',
    product: 'Online Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 300,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.alto.bmo.com/en-us/high-yield-online-savings/',
    rateSourceUrl: 'https://www.alto.bmo.com/en-us/high-yield-online-savings/',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'viobank',
    institution: 'Vio Bank',
    product: 'Online Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 399,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.viobank.com/online-savings-account',
    rateSourceUrl: 'https://www.viobank.com/online-savings-account',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'mybankingdirect',
    institution: 'My Banking Direct',
    product: 'High Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 390,
    minToEarn: 1,
    monthlyFee: 0,
    url: 'https://www.mybankingdirect.com/bank/products/high-yield-savings.html',
    rateSourceUrl: 'https://www.mybankingdirect.com/bank/products/high-yield-savings.html',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'etrade',
    institution: 'E*TRADE by Morgan Stanley',
    product: 'Premium Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 350,
    bonusBps: 50,
    bonusText: 'Intro rate for 6 months for new accounts',
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://us.etrade.com/bank/premium-savings-account',
    rateSourceUrl: 'https://us.etrade.com/bank/premium-savings-account',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'axos',
    institution: 'Axos Bank',
    product: 'ONE Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 100,
    bonusBps: 321,
    bonusText: 'Top rate requires paired ONE Checking with $1,500+/mo direct deposits',
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.axosbank.com/personal/bank/axos-one',
    rateSourceUrl: 'https://www.axosbank.com/personal/bank/axos-one',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'varo',
    institution: 'Varo Bank',
    product: 'Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 100,
    bonusBps: 275,
    bonusText: 'Top rate on first $5,000 only; requires $1,000+/mo direct deposits',
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.varomoney.com/high-yield-savings-account/',
    rateSourceUrl: 'https://www.varomoney.com/high-yield-savings-account/',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'tab',
    institution: 'TAB Bank',
    product: 'TAB Save High Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 361,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.tabbank.com/tab-save-high-yield-savings/',
    rateSourceUrl: 'https://www.tabbank.com/tab-save-high-yield-savings/',
    asOf: RATES_AS_OF,
    featured: true,
    region: 'UT',
  },
  {
    id: 'quontic',
    institution: 'Quontic Bank',
    product: 'High Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 320,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.quontic.com/high-yield-savings/',
    rateSourceUrl: 'https://www.quontic.com/high-yield-savings/',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'upgrade',
    institution: 'Upgrade',
    product: 'Premier Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    insuranceNote: 'FDIC insurance passed through Cross River Bank.',
    apyBps: 305,
    minToEarn: 1000,
    monthlyFee: 0,
    url: 'https://www.upgrade.com/premier-savings/',
    rateSourceUrl: 'https://www.upgrade.com/premier-savings/',
    asOf: RATES_AS_OF,
    featured: true,
  },
  // Big-bank reference accounts: in the picker so people can find their bank,
  // not featured in the best-rates table.
  {
    id: 'chase',
    institution: 'Chase',
    product: 'Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 1,
    minToEarn: 0,
    monthlyFee: 5,
    url: 'https://www.chase.com/personal/savings',
    rateSourceUrl: 'https://www.chase.com/personal/savings/savings-account/interest-rates',
    asOf: RATES_AS_OF,
    featured: false,
  },
  {
    id: 'bofa',
    institution: 'Bank of America',
    product: 'Advantage Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 1,
    minToEarn: 0,
    monthlyFee: 8,
    url: 'https://www.bankofamerica.com/deposits/savings-accounts',
    rateSourceUrl: 'https://www.bankofamerica.com/deposits/savings-accounts',
    asOf: RATES_AS_OF,
    featured: false,
  },
  {
    id: 'wellsfargo',
    institution: 'Wells Fargo',
    product: 'Way2Save Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 1,
    minToEarn: 0,
    monthlyFee: 5,
    url: 'https://www.wellsfargo.com/savings-cds/way2save',
    rateSourceUrl: 'https://www.wellsfargo.com/savings-cds/rates',
    asOf: RATES_AS_OF,
    featured: false,
  },
  {
    id: 'citi',
    institution: 'Citi',
    product: 'Accelerate Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 315,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.citi.com/banking/savings-account',
    rateSourceUrl: 'https://www.citi.com/banking/savings-account',
    asOf: RATES_AS_OF,
    featured: true,
  },
  {
    id: 'usbank',
    institution: 'U.S. Bank',
    product: 'Standard Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 1,
    minToEarn: 0,
    monthlyFee: 4,
    url: 'https://www.usbank.com/bank-accounts/savings-accounts.html',
    rateSourceUrl: 'https://www.usbank.com/bank-accounts/savings-accounts.html',
    asOf: RATES_AS_OF,
    featured: false,
  },

  {
    id: 'onpointcu',
    institution: 'OnPoint Community Credit Union',
    product: 'Premium Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '3.50% APY on first $100,000 only; requires 6 checking transactions/mo + Loyalty Savings',
    apyBps: 350,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.onpointcu.com/savings-accounts/onpoint-premium-savings/',
    rateSourceUrl: 'https://www.onpointcu.com/savings-accounts/onpoint-premium-savings/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'OR',
  },
  {
    id: 'americanheritage',
    institution: 'American Heritage Credit Union',
    product: 'High-Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: 'rate applies to balances $10,000+',
    apyBps: 310,
    minToEarn: 10000,
    monthlyFee: 0,
    url: 'https://www.americanheritagecu.org/rates',
    rateSourceUrl: 'https://www.americanheritagecu.org/rates',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'PA',
  },
  {
    id: 'vermontfederal',
    institution: 'Vermont Federal Credit Union',
    product: 'High Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '4.00% on first $5,000 only, 0.10% above; promo through 12/31/2026',
    apyBps: 400,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.vermontfederal.org/high-yield-savings-account-special-offer',
    rateSourceUrl: 'https://www.vermontfederal.org/high-yield-savings-account-special-offer',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'VT',
  },
  {
    id: 'langleyfcu',
    institution: 'Langley Federal Credit Union',
    product: 'High-Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '3.60% on balances up to $25,000, 0.05% above',
    apyBps: 360,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.langleyfcu.org/high-yield-savings',
    rateSourceUrl: 'https://www.langleyfcu.org/high-yield-savings',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'VA',
  },
  {
    id: 'dcu',
    institution: 'Digital Federal Credit Union (DCU)',
    product: 'Primary Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '5.00% APY on first $1,000 only; above earns 0.05%',
    apyBps: 500,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.dcu.org/bank/savings/primary-savings.html',
    rateSourceUrl: 'https://www.dcu.org/bank/savings/primary-savings.html',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'MA',
  },
  {
    id: 'meijercu',
    institution: 'Meijer Credit Union',
    product: 'High Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    apyBps: 345,
    minToEarn: 1000,
    monthlyFee: 0,
    url: 'https://www.meijercreditunion.com/high-yield-savings',
    rateSourceUrl: 'https://www.meijercreditunion.com/high-yield-savings',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'MI',
  },
  {
    id: 'wingscu',
    institution: 'Wings Credit Union',
    product: 'Elevate Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: 'up to 3.75% with $1,000+/mo direct deposit; $10,000 minimum',
    apyBps: 350,
    minToEarn: 10000,
    monthlyFee: 0,
    url: 'https://www.wingscu.com/rates/savings',
    rateSourceUrl: 'https://www.wingscu.com/rates/savings',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'MN',
  },
  {
    id: 'keesler',
    institution: 'Keesler Federal Credit Union',
    product: 'HIMMA Premier Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: 'promo for first 6 months on new deposits',
    apyBps: 325,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.kfcu.org/rates',
    rateSourceUrl: 'https://www.kfcu.org/rates',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'MS',
  },
  {
    id: 'communityamerica',
    institution: 'CommunityAmerica Credit Union',
    product: 'High Interest Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '5.00% on first $2,500 with monthly qualifiers',
    apyBps: 500,
    minToEarn: 1,
    monthlyFee: 0,
    url: 'https://www.communityamerica.com/personal/bank/accounts/savings/high-interest-savings-stl',
    rateSourceUrl: 'https://www.communityamerica.com/personal/bank/accounts/savings/high-interest-savings-stl',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'KS/MO',
  },
  {
    id: 'montanafcu',
    institution: 'Montana Federal Credit Union',
    product: 'Peak Rewards Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '3.00% on first $10,000 with checking qualifications',
    apyBps: 300,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.montanacu.com/save-up/personal-savings/peak-rewards-savings.html',
    rateSourceUrl: 'https://www.montanacu.com/save-up/personal-savings/peak-rewards-savings.html',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'MT',
  },
  {
    id: 'fnbodirect',
    institution: 'FNBO Direct',
    product: 'Online Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    bonusText: 'tiered up to 3.55% at $500k+',
    apyBps: 325,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.fnbodirect.com/money-market',
    rateSourceUrl: 'https://www.fnbodirect.com/money-market',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'NE',
  },
  {
    id: 'gncu',
    institution: 'Greater Nevada Credit Union',
    product: 'I Can Save',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '6.00% on first $2,500 only; monthly deposit required',
    apyBps: 600,
    minToEarn: 5,
    monthlyFee: 0,
    url: 'https://www.gncu.org/banking/account-rates/',
    rateSourceUrl: 'https://www.gncu.org/banking/account-rates/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'NV',
  },
  {
    id: 'servicecu',
    institution: 'Service Credit Union',
    product: 'Primary Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '5.00% on first $500 only; above earns 0.25%',
    apyBps: 500,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://servicecu.org/rates/',
    rateSourceUrl: 'https://servicecu.org/rates/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'NH',
  },
  {
    id: 'valleybank',
    institution: 'Valley Bank',
    product: 'Valley Direct High Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 379,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.valleydirect.com/Savings',
    rateSourceUrl: 'https://www.valleydirect.com/Savings',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'NJ',
  },
  {
    id: 'oneamericanbank',
    institution: 'One American Bank',
    product: 'Savings+',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    bonusText: 'tiered up to 3.30% at $50,000+',
    apyBps: 200,
    minToEarn: 100,
    monthlyFee: 2,
    url: 'https://www.oneamericanbank.com/personal/savings',
    rateSourceUrl: 'https://www.oneamericanbank.com/personal/savings',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'SD',
  },
  {
    id: 'ascendfcu',
    institution: 'Ascend Federal Credit Union',
    product: 'High-Yield Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    apyBps: 314,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://ascend.org/rates',
    rateSourceUrl: 'https://ascend.org/rates',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'TN',
  },
  {
    id: 'cutx',
    institution: 'Credit Union of Texas',
    product: 'High Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '3.25% on $1,000-$25,000; lower above',
    apyBps: 325,
    minToEarn: 1000,
    monthlyFee: 10,
    url: 'https://www.cutx.org/banking/high-yield-savings-account',
    rateSourceUrl: 'https://www.cutx.org/banking/high-yield-savings-account',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'TX',
  },
  {
    id: 'hawaiistatefcu',
    institution: 'Hawaii State Federal Credit Union',
    product: 'Always Saving',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '5.00% on first $5,000 with qualifications, 3.00% above',
    apyBps: 500,
    minToEarn: 100,
    monthlyFee: 5,
    url: 'https://hawaiistatefcu.com/rates/',
    rateSourceUrl: 'https://hawaiistatefcu.com/rates/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'HI',
  },
  {
    id: 'iccu',
    institution: 'Idaho Central Credit Union',
    product: 'High Yield Online Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: 'tiered up to 3.40%',
    apyBps: 340,
    minToEarn: 1000,
    monthlyFee: 0,
    url: 'https://www.iccu.com/rates/',
    rateSourceUrl: 'https://www.iccu.com/rates/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'ID',
  },
  {
    id: 'alliant',
    institution: 'Alliant Credit Union',
    product: 'High-Rate Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    apyBps: 310,
    minToEarn: 100,
    monthlyFee: 0,
    url: 'https://www.alliantcreditunion.org/bank/high-rate-savings',
    rateSourceUrl: 'https://www.alliantcreditunion.org/bank/high-rate-savings',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'IL',
  },
  {
    id: 'merchantsindiana',
    institution: 'Merchants Bank of Indiana',
    product: 'Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    bonusText: 'flat rate on all balances',
    apyBps: 350,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.bankmerchants.com/personal/money-market',
    rateSourceUrl: 'https://www.bankmerchants.com/personal/money-market',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'IN',
  },
  {
    id: 'greenstate',
    institution: 'GreenState Credit Union',
    product: 'Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '4.00% promo for first 6 months; $10,000 min',
    apyBps: 400,
    minToEarn: 10000,
    monthlyFee: 0,
    url: 'https://www.greenstate.org/rates/money-market-accounts.html',
    rateSourceUrl: 'https://www.greenstate.org/rates/money-market-accounts.html',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'IA',
  },
  {
    id: 'aboundcu',
    institution: 'Abound Credit Union',
    product: 'High-Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '4.25% on first $5,000 only',
    apyBps: 425,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.aboundcu.com/community-support/get-in-touch/rates/deposit-rates',
    rateSourceUrl: 'https://www.aboundcu.com/community-support/get-in-touch/rates/deposit-rates',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'KY',
  },
  {
    id: 'pelicancu',
    institution: 'Pelican State Credit Union',
    product: 'Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: 'tiered: 3.18% at $100k+, 4.21% at $200k+; $20,000 min',
    apyBps: 215,
    minToEarn: 20000,
    monthlyFee: 0,
    url: 'https://pelicancu.com/accounts/savings-accounts/money-market',
    rateSourceUrl: 'https://pelicancu.com/accounts/savings-accounts/money-market',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'LA',
  },
  {
    id: 'camdennational',
    institution: 'Camden National Bank',
    product: 'High-Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    bonusText: '3.10% at $250k+; requires Camden checking',
    apyBps: 265,
    minToEarn: 2500,
    monthlyFee: 0,
    url: 'https://www.camdennational.bank/rates',
    rateSourceUrl: 'https://www.camdennational.bank/rates',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'ME',
  },
  {
    id: 'cfgbank',
    institution: 'CFG Bank',
    product: 'High Yield Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    bonusText: 'flat rate',
    apyBps: 380,
    minToEarn: 1000,
    monthlyFee: 10,
    url: 'https://www.cfg.bank/our-mma-deposit-rates/',
    rateSourceUrl: 'https://www.cfg.bank/our-mma-deposit-rates/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'MD',
  },
  {
    id: 'capitalcu',
    institution: 'Capital Credit Union',
    product: 'Growth Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: 'requires $50,000+ average balance',
    apyBps: 304,
    minToEarn: 50000,
    monthlyFee: 10,
    url: 'https://www.capcu.org/personal/services-tools/rates/deposit-rates',
    rateSourceUrl: 'https://www.capcu.org/personal/services-tools/rates/deposit-rates',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'ND',
  },
  {
    id: 'cfbank',
    institution: 'CFBank',
    product: 'Platinum Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    bonusText: 'requires $25,000+ balance',
    apyBps: 330,
    minToEarn: 25000,
    monthlyFee: 10,
    url: 'https://www.cf.bank/Personal/Personal-Banking/Savings',
    rateSourceUrl: 'https://www.cf.bank/Personal/Personal-Banking/Savings',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'OH',
  },
  {
    id: 'okcu',
    institution: 'Oklahoma’s Credit Union',
    product: 'High-Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '4.00% on first $5,000 only, 0.10% above',
    apyBps: 400,
    minToEarn: 5,
    monthlyFee: 0,
    url: 'https://www.okcu.org/personal/resources/rates/savings-rates',
    rateSourceUrl: 'https://www.okcu.org/personal/resources/rates/savings-rates',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'OK',
  },
  {
    id: 'sunward',
    institution: 'Sunward Federal Credit Union',
    product: 'Power Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '5.04% on first $2,500 only; tiers drop above',
    apyBps: 504,
    minToEarn: 1,
    monthlyFee: 0,
    url: 'https://gosunward.org/rates',
    rateSourceUrl: 'https://gosunward.org/rates',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'NM',
  },
  {
    id: 'applebank',
    institution: 'Apple Bank',
    product: 'My Reserve Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    bonusText: '4.00% on balances up to $1M; NY/NJ/FL residents',
    apyBps: 400,
    minToEarn: 1000,
    monthlyFee: 0,
    url: 'https://www.applebank.com/personal/savings/my-reserve-savings',
    rateSourceUrl: 'https://www.applebank.com/personal/savings/my-reserve-savings',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'NY',
  },
  {
    id: 'liveoakbank',
    institution: 'Live Oak Bank',
    product: 'Personal High-Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    apyBps: 390,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.liveoak.bank/savings/',
    rateSourceUrl: 'https://www.liveoak.bank/savings/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'NC',
  },
  {
    id: 'greenwoodcu',
    institution: 'Greenwood Credit Union',
    product: 'Premier Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: 'requires $50,000+ balance',
    apyBps: 300,
    minToEarn: 50000,
    monthlyFee: 0,
    url: 'https://greenwoodcu.org/rates/',
    rateSourceUrl: 'https://greenwoodcu.org/rates/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'RI',
  },
  {
    id: 'sharonview',
    institution: 'Sharonview Federal Credit Union',
    product: 'High-Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '3.20% on $100-$100,000; requires $100+/mo deposits',
    apyBps: 320,
    minToEarn: 100,
    monthlyFee: 0,
    url: 'https://www.sharonview.org/resources/rates-fees',
    rateSourceUrl: 'https://www.sharonview.org/resources/rates-fees',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'SC',
  },
  {
    id: 'redfcu',
    institution: 'Redstone Federal Credit Union',
    product: 'Brighter Day Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '4.06% on first $2,500 only, 0.25% above',
    apyBps: 406,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.redfcu.org/personal/resources/rates/',
    rateSourceUrl: 'https://www.redfcu.org/personal/resources/rates/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'AL',
  },
  {
    id: 'firstbankak',
    institution: 'First Bank Alaska',
    product: 'Prime Saver Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    bonusText: '$20,000 minimum',
    apyBps: 330,
    minToEarn: 20000,
    monthlyFee: 0,
    url: 'https://www.firstbankak.com/services-tools/tools/rates/Deposit-rates.html',
    rateSourceUrl: 'https://www.firstbankak.com/services-tools/tools/rates/Deposit-rates.html',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'AK',
  },
  {
    id: 'oneazcu',
    institution: 'OneAZ Credit Union',
    product: 'Propel High-Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '3.75% on first $10,000 only',
    apyBps: 375,
    minToEarn: 100,
    monthlyFee: 0,
    url: 'https://www.oneazcu.com/personal/banking/savings/high-yield-savings/',
    rateSourceUrl: 'https://www.oneazcu.com/personal/banking/savings/high-yield-savings/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'AZ',
  },
  {
    id: 'truservice',
    institution: 'TruService Community FCU',
    product: 'Money Market Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '2.80% at $100k+ tier',
    apyBps: 280,
    minToEarn: 2500,
    monthlyFee: 0,
    url: 'https://www.truservice.net/rates',
    rateSourceUrl: 'https://www.truservice.net/rates',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'AR',
  },
  {
    id: 'starone',
    institution: 'Star One Credit Union',
    product: 'Money Market Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    apyBps: 345,
    minToEarn: 50,
    monthlyFee: 0,
    url: 'https://www.starone.org/bank/money-market-savings',
    rateSourceUrl: 'https://www.starone.org/bank/money-market-savings',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'CA',
  },
  {
    id: 'bellco',
    institution: 'Bellco Credit Union',
    product: 'Premier Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '3.25% at $50k+',
    apyBps: 325,
    minToEarn: 10000,
    monthlyFee: 0,
    url: 'https://www.bellco.org/personal/personal-banking/saving/premier-money-market-account/',
    rateSourceUrl: 'https://www.bellco.org/personal/personal-banking/saving/premier-money-market-account/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'CO',
  },
  {
    id: 'libertybank',
    institution: 'Liberty Bank',
    product: 'High-Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    bonusText: '3.35% at $250k+ tier',
    apyBps: 335,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.liberty-bank.com/personal/banking/high-yield-savings',
    rateSourceUrl: 'https://www.liberty-bank.com/personal/banking/high-yield-savings',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'CT',
  },
  {
    id: 'wsfsbank',
    institution: 'WSFS Bank',
    product: 'High-Yield Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    bonusText: '3.00% at $100k+ tier',
    apyBps: 300,
    minToEarn: 10000,
    monthlyFee: 15,
    url: 'https://www.wsfsbank.com/personal/banking/savings/high-yield-money-market/',
    rateSourceUrl: 'https://www.wsfsbank.com/personal/banking/savings/high-yield-money-market/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'DE',
  },
  {
    id: 'fairwinds',
    institution: 'FAIRWINDS Credit Union',
    product: 'Emergency Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '5.00% on first $1,000 only, 0.10% above',
    apyBps: 500,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.fairwinds.org/downloads/truth-in-savings-personal',
    rateSourceUrl: 'https://www.fairwinds.org/downloads/truth-in-savings-personal',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'FL',
  },
  {
    id: 'gucu',
    institution: 'Georgia United Credit Union',
    product: 'High Earning Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: 'requires $100+/mo deposit or $25k+ balance',
    apyBps: 315,
    minToEarn: 100,
    monthlyFee: 0,
    url: 'https://gucu.org/bank/savings',
    rateSourceUrl: 'https://gucu.org/bank/savings',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'GA',
  },
  {
    id: 'qualstarcu',
    institution: 'Qualstar Credit Union',
    product: 'Premier Money Market',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: 'requires eStatements',
    apyBps: 351,
    minToEarn: 1000,
    monthlyFee: 0,
    url: 'https://www.qualstarcu.com/money-market-rates',
    rateSourceUrl: 'https://www.qualstarcu.com/money-market-rates',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'WA',
  },
  {
    id: 'wesbanco',
    institution: 'WesBanco',
    product: 'Money Market Special',
    type: 'BANK_ACCOUNT',
    insurance: 'FDIC',
    bonusText: 'promo requires $25,000+ new money',
    apyBps: 375,
    minToEarn: 25000,
    monthlyFee: 10,
    url: 'https://www.wesbanco.com/cd-money-market-special-offer/',
    rateSourceUrl: 'https://www.wesbanco.com/cd-money-market-special-offer/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'WV',
  },
  {
    id: 'covantagecu',
    institution: 'CoVantage Credit Union',
    product: 'MaxYield High Yield Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: 'requires direct deposit + 4 card transactions/cycle',
    apyBps: 325,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.covantagecu.org/accounts/maxyield-high-yield-savings',
    rateSourceUrl: 'https://www.covantagecu.org/accounts/maxyield-high-yield-savings',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'WI',
  },
  {
    id: 'bluefcu',
    institution: 'Blue Federal Credit Union',
    product: 'Accelerated Savings',
    type: 'BANK_ACCOUNT',
    insurance: 'NCUA',
    bonusText: '5.00% on first $1,000 only; tiers drop fast above',
    apyBps: 500,
    minToEarn: 0,
    monthlyFee: 0,
    url: 'https://www.bluefcu.com/bank/savings/accelerated-savings/',
    rateSourceUrl: 'https://www.bluefcu.com/bank/savings/accelerated-savings/',
    asOf: RATES_AS_OF,
    featured: false,
    region: 'WY',
  },
];

export const FEATURED = ACCOUNTS.filter((a) => a.featured);
export const PICKER_ACCOUNTS = ACCOUNTS.filter((a) => !a.isReserve);
